import { type LocalTool, localTools } from "@pax/environments";
import {
  cn,
  growOrigin,
  HapticSurface,
  MorphPanel,
  OptionRow,
  PanelHeading,
  PanelTile,
  panelRow,
  solidSurface,
  usePanelDismiss,
} from "@pax/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { ArrowUpRight, ChevronDown, UserRoundX } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { DevelopmentTool, DevelopmentToolsVisibility } from "@/development/preferences";
import { type ActAsTarget, actAs, canActAs, isActingAs, stopActing } from "@/features/auth/act-as";
import { ActAsControls, FindUserDialog } from "@/features/auth/components/ActAsControls";
import { useSession } from "@/features/auth/queries";
import { localStack } from "@/lib/local-stack";

/**
 * Developer controls behind a chevron beside the logo. Developers get Act as in
 * every environment, and an acting session gets Stop acting. On localhost the
 * menu also links the laptop's other local tools and toggles the page's
 * development tools.
 *
 * The panel is built like the game's control center: the same heading, and
 * tiles for the local Workers, Web tools, and Act as, at the bottom, that each
 * grow in place into their own pane. It sits over a
 * page rather than a map, so it wears the solid surface.
 *
 * It hangs off the frame rather than off the chevron, because the head is
 * clipped to the column and the brand travels on a translate that would pin
 * a fixed panel inside that clip.
 */

type View = "menu" | "actAs" | "workers" | "tools";

const VIEW_LABELS: Record<View, string> = {
  menu: "Developer",
  actAs: "Act as",
  workers: "Workers",
  tools: "Web tools",
};

const TOOLS: { tool: DevelopmentTool; name: string; description: string }[] = [
  {
    tool: "botChecks",
    name: "Bot checks",
    description: "Inspect verification and queue Castle's next verdict.",
  },
  {
    tool: "tanstackDevtools",
    name: "TanStack Devtools",
    description: "Session, queries, and routes in one panel.",
  },
  {
    tool: "reactGrab",
    name: "React Grab",
    description: "Copy an element's source context for an agent.",
  },
  {
    tool: "reactScan",
    name: "React Scan",
    description: "Outlines components as they render.",
  },
  {
    tool: "viteDevtools",
    name: "Vite DevTools",
    description: "Modules, plugins, transforms, and terminals.",
  },
];

type DeveloperMenuProps = {
  developmentTools: DevelopmentToolsVisibility;
  onDevelopmentToolVisibilityChange: (tool: DevelopmentTool, visible: boolean) => void;
};

export function DeveloperMenu({
  developmentTools,
  onDevelopmentToolVisibilityChange,
}: DeveloperMenuProps) {
  const { data: session } = useSession();
  const local = import.meta.env.DEV && window.location.hostname.endsWith("localhost");
  const acting = isActingAs(session);
  const developer = canActAs(session) && !acting;
  if (!local && !developer && !acting) return null;
  return (
    <DeveloperPanel
      local={local}
      developer={developer}
      acting={acting}
      developmentTools={developmentTools}
      onDevelopmentToolVisibilityChange={onDevelopmentToolVisibilityChange}
    />
  );
}

function DeveloperPanel({
  local,
  developer,
  acting,
  developmentTools,
  onDevelopmentToolVisibilityChange,
}: DeveloperMenuProps & { local: boolean; developer: boolean; acting: boolean }) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>("menu");
  const [growFrom, setGrowFrom] = useState("center");
  const [findUserOpen, setFindUserOpen] = useState(false);
  const paneRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setView("menu");
  }, []);

  const switchUser = useMutation({
    mutationFn: async (target: ActAsTarget | null) => {
      if (target) await actAs(queryClient, target.id);
      else await stopActing(queryClient);
    },
    onSuccess: async () => {
      setFindUserOpen(false);
      close();
      await router.invalidate();
      await navigate({ to: "/", replace: true });
    },
  });

  const panelRef = usePanelDismiss<HTMLDivElement>({
    open,
    onDismiss: close,
    trigger: triggerRef,
  });

  // Listing the app you are already in would be a link to nowhere.
  const tools = localTools(localStack).filter(
    tool => new URL(tool.href).host !== window.location.host,
  );
  const toolsOn = TOOLS.filter(({ tool }) => developmentTools[tool]).map(({ name }) => name);
  const workers = tools.filter(tool => tool.group === "Workers");

  /** Grows a tile in place into its own pane. */
  function openView(next: View, tile: HTMLElement) {
    setGrowFrom(growOrigin(paneRef.current, tile));
    setView(next);
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Developer menu"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
        className={cn(
          "rounded-md p-1 text-muted-foreground transition hover:text-foreground",
          open && "text-foreground",
        )}
      >
        <ChevronDown className="size-3.5" aria-hidden />
      </button>

      {open &&
        createPortal(
          <MorphPanel
            ref={panelRef}
            view={view}
            anchor="top-left"
            growFrom={growFrom}
            paneRef={paneRef}
            className={cn(
              "overlay-panel fixed top-(--bar-height) left-3 z-50 text-sm",
              solidSurface,
            )}
          >
            <div className="w-[18rem] p-2">
              <PanelHeading
                label={VIEW_LABELS[view]}
                onBack={view === "menu" ? undefined : () => setView("menu")}
                onClose={close}
              />
              {view === "menu" ? (
                <div className="flex flex-col gap-2">
                  {acting ? (
                    <button
                      type="button"
                      disabled={switchUser.isPending}
                      onClick={() => switchUser.mutate(null)}
                      className={cn(panelRow, "disabled:pointer-events-none disabled:opacity-50")}
                    >
                      <UserRoundX aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1 truncate">Stop acting</span>
                      <HapticSurface />
                    </button>
                  ) : null}
                  {switchUser.error && !findUserOpen ? (
                    <p role="alert" className="px-2 text-xs text-destructive">
                      {switchUser.error.message}
                    </p>
                  ) : null}
                  {local
                    ? (["Sites", "Data"] as const).map(group => (
                        <div key={group} className="flex flex-col gap-0.5">
                          <p className="px-2 pt-1 text-xs font-medium text-muted-foreground">
                            {group}
                          </p>
                          <ToolLinks
                            tools={tools.filter(tool => tool.group === group)}
                            onOpen={close}
                          />
                        </div>
                      ))
                    : null}
                  {local && workers.length > 0 ? (
                    <PanelTile label="Workers" onOpen={tile => openView("workers", tile)}>
                      <span className="text-sm font-medium">
                        {workers.length === 1 ? "1 worker" : `${workers.length} workers`}
                      </span>
                    </PanelTile>
                  ) : null}
                  {local ? (
                    <PanelTile label="Web tools" onOpen={tile => openView("tools", tile)}>
                      <span className="text-sm font-medium">
                        {toolsOn.length === 0 ? "All off" : toolsOn.join(" · ")}
                      </span>
                    </PanelTile>
                  ) : null}
                  {developer ? (
                    <PanelTile label="Act as" onOpen={tile => openView("actAs", tile)}>
                      <span className="text-sm font-medium">Personas and users</span>
                    </PanelTile>
                  ) : null}
                </div>
              ) : view === "actAs" ? (
                <div className="flex flex-col gap-0.5 rounded-xl bg-foreground/5 p-1">
                  <ActAsControls
                    pending={switchUser.isPending}
                    onActAs={target => switchUser.mutate(target)}
                    onFindUser={() => {
                      switchUser.reset();
                      close();
                      setFindUserOpen(true);
                    }}
                  />
                  {switchUser.error && !findUserOpen ? (
                    <p role="alert" className="px-2 pb-1 text-xs text-destructive">
                      {switchUser.error.message}
                    </p>
                  ) : null}
                </div>
              ) : view === "workers" ? (
                <div className="flex flex-col gap-0.5 rounded-xl bg-foreground/5 p-1">
                  <ToolLinks tools={workers} onOpen={close} />
                </div>
              ) : (
                <div className="flex flex-col gap-0.5 rounded-xl bg-foreground/5 p-2">
                  {TOOLS.map(({ tool, name, description }) => (
                    <OptionRow
                      key={tool}
                      selected={developmentTools[tool]}
                      onSelect={() =>
                        onDevelopmentToolVisibilityChange(tool, !developmentTools[tool])
                      }
                      name={name}
                      description={description}
                    />
                  ))}
                </div>
              )}
            </div>
          </MorphPanel>,
          document.body,
        )}

      {developer ? (
        <FindUserDialog
          open={findUserOpen}
          pending={switchUser.isPending}
          onOpenChange={setFindUserOpen}
          onActAs={target => switchUser.mutate(target)}
          error={switchUser.error?.message ?? null}
        />
      ) : null}
    </>
  );
}

/** Links to the laptop's other local tools, each opening in a new tab. */
function ToolLinks({ tools, onOpen }: { tools: LocalTool[]; onOpen: () => void }) {
  return tools.map(tool => (
    <a
      key={tool.href}
      href={tool.href}
      target="_blank"
      rel="noreferrer"
      onClick={onOpen}
      className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm transition hover:bg-foreground/10 active:scale-[0.99]"
    >
      <span className="min-w-0 flex-1 truncate">{tool.label}</span>
      <ArrowUpRight aria-hidden className="size-4 shrink-0 text-muted-foreground" />
    </a>
  ));
}
